import { useCallback, useMemo } from 'react';
import { useIntl } from 'react-intl';
import { useNavigate } from 'react-router';

import { useGetSubscriptionsInfiniteQuery } from 'src/apiClients';
import type { Subscription } from 'src/apiClients/queries';
import {
    SortDirection,
    type DataTableColDef,
    type DataTableRow,
} from 'src/components/data-table/DataTable.types';
import { EntityListPage } from 'src/components/entity-list-page';
import { ROUTES } from 'src/utils/strings';

import { i18n } from './InstancesPage.i18n';

const SEARCH_COLUMNS = [
    'subscriptionName',
    'customerName',
    'companyName',
    'salesforceLink',
    'capacityGroupName',
];
const INITIAL_SORT = [{ field: 'state', direction: SortDirection.Asc }];

const mapRow = (sub: Subscription): DataTableRow => ({
    id: sub.id,
    subscriptionName: sub.subscriptionName,
    customerName: sub.customerName,
    companyName: sub.companyName,
    salesforceLink: sub.salesforceLink,
    capacityGroupName: sub.capacityGroupName,
    capacityGroupId: sub.capacityGroupId,
    comment: sub.comment,
    state: sub.state,
    tenantId: sub.tenantId,
});

export const InstancesPage = () => {
    const intl = useIntl();
    const navigate = useNavigate();

    const columns: DataTableColDef[] = useMemo(
        () => [
            { field: 'subscriptionName', headerName: intl.formatMessage(i18n.displayName) },
            { field: 'customerName', headerName: intl.formatMessage(i18n.accountName) },
            { field: 'companyName', headerName: intl.formatMessage(i18n.legalName) },
            { field: 'salesforceLink', headerName: intl.formatMessage(i18n.salesforceId) },
            { field: 'capacityGroupName', headerName: intl.formatMessage(i18n.capacityGroupName) },
            { field: 'capacityGroupId', headerName: intl.formatMessage(i18n.capacityGroupId) },
            {
                field: 'state',
                headerName: intl.formatMessage(i18n.status),
                width: 160,
                filterOptions: [
                    { label: intl.formatMessage(i18n.active), value: 'A' },
                    { label: intl.formatMessage(i18n.archived), value: 'X' },
                    { label: intl.formatMessage(i18n.inactive), value: 'I' },
                ],
                valueFormatter: (value) => {
                    if (value === 'A') return intl.formatMessage(i18n.active);
                    if (value === 'X') return intl.formatMessage(i18n.archived);
                    return intl.formatMessage(i18n.inactive);
                },
            },
        ],
        [intl],
    );

    const handleRowClick = useCallback(
        (row: DataTableRow) => {
            navigate(`${ROUTES.INSTANCES}/${row.id}`);
        },
        [navigate],
    );

    return (
        <EntityListPage<Subscription>
            automationId="instances-page"
            useQuery={useGetSubscriptionsInfiniteQuery}
            searchColumns={SEARCH_COLUMNS}
            initialSort={INITIAL_SORT}
            columns={columns}
            mapRow={mapRow}
            onRowClick={handleRowClick}
            titleWhenNoResults={intl.formatMessage(i18n.noInstancesTitle)}
            messageWhenNoResults={intl.formatMessage(i18n.noInstancesMessage)}
            titleWhenLoadError={intl.formatMessage(i18n.loadErrorTitle)}
            messageWhenLoadError={intl.formatMessage(i18n.loadErrorMessage)}
        />
    );
};
